import { handleFormError, handleSubmit } from "app/client/lib/formUtils";
import { reportError } from "app/client/models/errors";
import { LoginAPI } from "app/client/ui/LoginAPI";
import * as loginCss from "app/client/ui/LoginPagesCss";
import * as mfaCss from "app/client/ui/MFAConfigCss";
import { textButton } from "app/client/ui2018/buttons";
import { cssLink } from "app/client/ui2018/links";
import { loadingSpinner } from "app/client/ui2018/loaders";
import { IModalControl } from "app/client/ui2018/modals";
import { AuthMethod, ChallengeRequired, VerifyPasswordResponse } from "app/common/LoginAPI";

import { dom, IDisposableOwner, makeTestId, Observable, styled } from "grainjs";
import { toDataURL } from "qrcode";

const testId = makeTestId("test-mfa-");

interface SecurityVerificationFormOptions {
  // Called on successful completion of the security verification forms.
  onSuccess: (info: { password: string }) => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
}

/**
 * Builds security verification forms, including a password form and optional 2FA
 * verification form.
 *
 * @param {SecurityVerificationFormOptions} options Form options.
 */
export function buildSecurityVerificationForms(
  owner: IDisposableOwner,
  { onSuccess, loginAPI, ctl }: SecurityVerificationFormOptions,
) {
  const currentStep = Observable.create<"password" | "second-step">(owner, "password");
  const password = Observable.create(owner, "");

  let challengeDetails: ChallengeRequired;

  return dom.domComputed(currentStep, (step) => {
    switch (step) {
      case "password": {
        return dom.create(buildVerifyPasswordForm, {
          password,
          loginAPI,
          ctl,
          onSuccess: (result) => {
            if (!result.isChallengeRequired) {
              return onSuccess({ password: password.get() });
            }

            challengeDetails = result;
            currentStep.set("second-step");
          },
        });
      }
      case "second-step": {
        return dom.create(buildVerifySecondStepForm, {
          theme: "account",
          ctl,
          challengeDetails,
          onSuccess: () => onSuccess({ password: password.get() }),
          onSubmit: (code, session, method) => loginAPI.verifySecondStep(method, code, session),
          onResendCode: async () => {
            const result = await loginAPI.verifyPassword(password.get(), "SMS");
            if (!result.isChallengeRequired) { throw new Error("Unknown error occurred."); }

            return result;
          },
          onUseAuthApp: async () => {
            const result = await loginAPI.verifyPassword(password.get(), "TOTP");
            if (!result.isChallengeRequired) { throw new Error("Unknown error occurred."); }

            return result;
          },
          onUseSMS: async () => {
            const result = await loginAPI.verifyPassword(password.get(), "SMS");
            if (!result.isChallengeRequired) { throw new Error("Unknown error occurred."); }

            return result;
          },
        });
      }
    }
  });
}

interface VerifyPasswordFormOptions {
  // Observable value of the password input.
  password: Observable<string>;
  // Called on successful completion of the form.
  onSuccess: (result: VerifyPasswordResponse) => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
}

export function buildVerifyPasswordForm(
  owner: IDisposableOwner,
  { onSuccess, loginAPI, ctl, password }: VerifyPasswordFormOptions,
) {
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  return dom.frag(
    mfaCss.formHeading("Confirm your password", testId("title")),
    mfaCss.formBody(
      dom("form",
        mfaCss.formInstructions("Please confirm your password to continue."),
        dom.maybe(errorObs, err => mfaCss.formError(err, testId("form-error"))),
        mfaCss.formLabel("Password", { for: "current-password" }),
        mfaCss.input(password,
          {
            id: "current-password",
            name: "password",
            placeholder: "password",
            type: "password",
            autocomplete: "current-password",
            required: "true",
          },
          (el) => { setTimeout(() => el.focus(), 10); },
          testId("password-input"),
        ),
        handleSubmit({
          pending,
          onSubmit: ({ password: pass }) => loginAPI.verifyPassword(pass),
          onSuccess: result => onSuccess(result),
          onError: err => handleFormError(err, errorObs),
        }),
        mfaCss.formButtons(
          mfaCss.bigPrimaryButton("Confirm", dom.boolAttr("disabled", pending), testId("confirm")),
          mfaCss.bigBasicButton("Cancel", dom.on("click", () => ctl.close()), testId("cancel")),
        ),
      ),
    ),
  );
}

// Common HTML input options for 6 digit verification fields (SMS and TOTP).
const verificationCodeInputOpts = {
  id: "verificationCode",
  name: "verificationCode",
  type: "text",
  inputmode: "numeric",
  pattern: "\\d{6}",
  required: "true",
  maxlength: "6",
};

interface VerifySecondStepFormOptions {
  // Specifies the styling of the form.
  theme: "login" | "account";
  // Called on successful completion of the form.
  onSuccess: (redirectUrl?: string) => void;
  // Called on submission of the form.
  onSubmit: (code: string, session: string, authMethod: AuthMethod) => Promise<any>;
  // Called when resend SMS code is clicked.
  onResendCode: () => Promise<ChallengeRequired>;
  // Called when switching from SMS to authenticator app.
  onUseAuthApp: () => Promise<ChallengeRequired>;
  // Called when switching from authenticator app to SMS.
  onUseSMS: () => Promise<ChallengeRequired>;
  // Info about the pre-selected verification challenge (TOTP or SMS).
  challengeDetails: ChallengeRequired;
  // If the form is embedded in a modal, the modal control (for closing via Cancel buttons).
  ctl?: IModalControl;
  // If true, shows a back text button in the footer of the form.
  showBackButton?: boolean;
  // The text to use for the back button. Defaults to '← Back'.
  backButtonText?: string;
  // Called when the back button is clicked.
  onBack?: () => void;
}

/**
 * Builds a form for finishing authentication via a 2FA method (SMS or TOTP).
 *
 * @param {VerifySecondStepFormOptions} options Form options.
 */
export function buildVerifySecondStepForm(
  owner: IDisposableOwner,
  {
    theme,
    onSuccess,
    onSubmit,
    onResendCode,
    onUseAuthApp,
    onUseSMS,
    ctl,
    challengeDetails,
    showBackButton,
    backButtonText,
    onBack,
  }: VerifySecondStepFormOptions,
) {
  const currentChallenge = Observable.create(owner, challengeDetails);

  return dom.domComputed(currentChallenge, ({ challengeName }) => {
    switch (challengeName) {
      case "SMS_MFA": {
        return dom.create(buildVerifyPhoneMessageForm, {
          theme,
          onSuccess: redirectUrl => onSuccess(redirectUrl),
          onSubmit: (code: string, session: string) => onSubmit(code, session, "SMS"),
          onResendCode: () => onResendCode(),
          onUseAuthApp: () => onUseAuthApp(),
          ctl,
          challenge: currentChallenge,
          showBackButton,
          backButtonText,
          onBack,
        });
      }
      case "SOFTWARE_TOKEN_MFA": {
        return dom.create(buildVerifyAuthAppForm, {
          theme,
          onSuccess: redirectUrl => onSuccess(redirectUrl),
          onSubmit: (code: string, session: string) => onSubmit(code, session, "TOTP"),
          onUseSMS: () => onUseSMS(),
          ctl,
          challenge: currentChallenge,
          showBackButton,
          backButtonText,
          onBack,
        });
      }
    }
  });
}

interface VerifyPhoneMessageFormOptions {
  // Specifies the styling of the form.
  theme: "login" | "account";
  // Called on successful completion of the form.
  onSuccess: (redirectUrl?: string) => void;
  // Called on submission for the form.
  onSubmit: (code: string, session: string) => Promise<any>;
  // Called when resend SMS code is clicked.
  onResendCode: () => Promise<ChallengeRequired>;
  // Called when switching from SMS to authenticator app.
  onUseAuthApp: () => Promise<ChallengeRequired>;
  // The observable value of the current SMS 2FA challenge.
  challenge: Observable<ChallengeRequired>;
  // If the form is embedded in a modal, the modal control (for closing via Cancel buttons).
  ctl?: IModalControl;
  // If true, shows a back text button in the footer of the form.
  showBackButton?: boolean;
  // The text to use for the back button. Defaults to '← Back'.
  backButtonText?: string;
  // Called when the back button is clicked.
  onBack?: () => void;
}

function buildVerifyPhoneMessageForm(
  owner: IDisposableOwner,
  {
    theme,
    onSuccess,
    onSubmit,
    onResendCode,
    onUseAuthApp,
    ctl,
    challenge,
    showBackButton,
    backButtonText,
    onBack,
  }: VerifyPhoneMessageFormOptions,
) {
  const css = theme === "account" ? mfaCss : loginCss;
  let challengeInfo = challenge.get();

  const verificationCode = Observable.create(owner, "");
  const isLoading = Observable.create(owner, false);
  const resentCode = Observable.create(owner, false);
  const sessionObs = Observable.create(owner, challengeInfo.session);
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  owner.autoDispose(isLoading.addListener((loading) => {
    if (!loading) { return; }

    errorObs.set(null);
    verificationCode.set("");
  }));

  return dom.domComputed(isLoading, (loading) => {
    if (loading) { return css.spinner(loadingSpinner()); }

    return [
      css.formHeading(
        dom.text(use => use(resentCode) ? "Sent again!" : "Almost there!"),
        testId("title"),
      ),
      css.formBody(
        dom("form",
          css.formInstructions(
            css.text(
              "We have sent an authentication code to ",
              cssLightlyBoldedText(challengeInfo.deliveryDestination),
              ".",
            ),
            css.text("Enter it below to confirm your account."),
            testId("main-text"),
          ),
          dom.maybe(errorObs, err => css.formError(err, testId("form-error"))),
          css.formLabel("Authentication Code", { for: "verificationCode" }),
          css.codeInput(verificationCode,
            { ...verificationCodeInputOpts, autocomplete: "one-time-code" },
            (el) => { setTimeout(() => el.focus(), 10); },
            testId("verification-code-input"),
          ),
          css.input(sessionObs, { name: "session", type: "hidden" }),
          css.resendCode("Didn't receive a code? ",
            css.textButton("Resend it",
              textButton.cls("-hover-bg-padding-sm"),
              { type: "button" },
              dom.on("click", async () => {
                try {
                  isLoading.set(true);
                  const result = await onResendCode();
                  resentCode.set(true);
                  sessionObs.set(result.session);
                  challengeInfo = result;
                }
                catch (err) {
                  handleFormError(err, errorObs);
                }
                finally {
                  isLoading.set(false);
                }
              }),
              dom.boolAttr("disabled", pending),
              testId("resend-code"),
            ),
          ),
          handleSubmit({
            pending,
            onSubmit: ({ verificationCode: code, session: s }) => onSubmit(code, s),
            onSuccess: result => onSuccess(result?.redirectUrl),
            onError: err => handleFormError(err, errorObs),
          }),
          css.formButtons(
            css.bigPrimaryButton("Submit", dom.boolAttr("disabled", pending), testId("submit")),
            !ctl ? null : mfaCss.bigBasicButton("Cancel",
              dom.on("click", () => ctl.close()),
              testId("cancel"),
            ),
          ),
          !challengeInfo.isAlternateChallengeAvailable && !showBackButton ? null : css.formFooter(
            cssFooterButtons(
              !challengeInfo.isAlternateChallengeAvailable ? null : css.textButton(
                "Use code from authenticator app?",
                { type: "button" },
                dom.on("click", async () => {
                  try {
                    isLoading.set(true);
                    challenge.set(await onUseAuthApp());
                  }
                  catch (err) {
                    handleFormError(err, errorObs);
                    isLoading.set(false);
                  }
                }),
                dom.boolAttr("disabled", pending),
                testId("use-auth-app"),
              ),
              !showBackButton || !onBack ? null : css.textButton(backButtonText ?? "← Back",
                { type: "button" },
                dom.on("click", () => onBack()),
                testId("back"),
              ),
            ),
          ),
        ),
      ),
    ];
  });
}

interface VerifyAuthAppFormOptions {
  // Specifies the styling of the form.
  theme: "login" | "account";
  // Called on successful completion of the form.
  onSuccess: (redirectUrl?: string) => void;
  // Called on submission for the form.
  onSubmit: (code: string, session: string) => Promise<any>;
  // Called when switching from authenticator app to SMS.
  onUseSMS: () => Promise<ChallengeRequired>;
  // The observable value of the current TOTP 2FA challenge.
  challenge: Observable<ChallengeRequired>;
  // If the form is embedded in a modal, the modal control (for closing via Cancel buttons).
  ctl?: IModalControl;
  // If true, shows a back text button in the footer of the form.
  showBackButton?: boolean;
  // The text to use for the back button. Defaults to '← Back'.
  backButtonText?: string;
  // Called when the back button is clicked.
  onBack?: () => void;
}

function buildVerifyAuthAppForm(
  owner: IDisposableOwner,
  {
    theme,
    onSuccess,
    onSubmit,
    onUseSMS,
    ctl,
    challenge,
    showBackButton,
    backButtonText,
    onBack,
  }: VerifyAuthAppFormOptions,
) {
  const { deliveryDestination, isAlternateChallengeAvailable, session } = challenge.get();

  const verificationCode = Observable.create(owner, "");
  const isLoading = Observable.create(owner, false);
  const sessionObs = Observable.create(owner, session);
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  const css = theme === "account" ? mfaCss : loginCss;

  owner.autoDispose(isLoading.addListener((loading) => {
    if (!loading) { return; }

    errorObs.set(null);
    verificationCode.set("");
  }));

  return dom.domComputed(isLoading, (loading) => {
    if (loading) { return css.spinner(loadingSpinner()); }

    return [
      css.formHeading("Almost there!", testId("title")),
      css.formBody(
        dom("form",
          css.formInstructions(
            "Enter the authentication code generated by your app to confirm your account.",
            testId("main-text"),
          ),
          dom.maybe(errorObs, err => css.formError(err, testId("form-error"))),
          css.formLabel("Verification Code", { for: "verificationCode" }),
          css.codeInput(verificationCode,
            verificationCodeInputOpts,
            (el) => { setTimeout(() => el.focus(), 10); },
            testId("verification-code-input"),
          ),
          css.input(sessionObs, { name: "session", type: "hidden" }),
          handleSubmit({
            pending,
            onSubmit: ({ verificationCode: code, session: s }) => onSubmit(code, s),
            onSuccess: result => onSuccess(result?.redirectUrl),
            onError: err => handleFormError(err, errorObs),
          }),
          css.formButtons(
            css.bigPrimaryButton("Submit", dom.boolAttr("disabled", pending), testId("submit")),
            !ctl ? null : mfaCss.bigBasicButton("Cancel",
              dom.on("click", () => ctl.close()),
              testId("cancel"),
            ),
          ),
          !isAlternateChallengeAvailable && !showBackButton ? null : css.formFooter(
            cssFooterButtons(
              !isAlternateChallengeAvailable ? null : dom("div",
                "Receive a code by SMS? ",
                css.textButton(`Text ${deliveryDestination}.`,
                  { type: "button" },
                  dom.on("click", async () => {
                    try {
                      isLoading.set(true);
                      challenge.set(await onUseSMS());
                    }
                    catch (err) {
                      handleFormError(err, errorObs);
                      isLoading.set(false);
                    }
                  }),
                  dom.boolAttr("disabled", pending),
                  testId("use-sms"),
                ),
              ),
              !showBackButton || !onBack ? null : css.textButton(backButtonText ?? "← Back",
                { type: "button" },
                dom.on("click", () => onBack()),
                testId("back"),
              ),
            ),
          ),
        ),
      ),
    ];
  });
}

interface ConfigureAuthAppFormOptions {
  // Called on successful completion of the form.
  onSuccess: () => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
  // The email of the current user. Encoded in the QR code.
  email: string;
}

/**
 * Builds a form for registering a software token (TOTP) MFA method.
 *
 * @param {ConfigureAuthAppFormOptions} options Form options.
 */
export function buildConfigureAuthAppForm(
  owner: IDisposableOwner,
  { onSuccess, loginAPI, ctl, email }: ConfigureAuthAppFormOptions,
) {
  const qrCode: Observable<string | null> = Observable.create(owner, null);
  const verificationCode = Observable.create(owner, "");
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  loginAPI.registerSoftwareToken()
    .then(async ({ secretCode }) => {
      const qrCodeUrl = `otpauth://totp/${encodeURI(`Grist:${email}`)}?secret=${secretCode}&issuer=Grist`;
      const qrCodeImg = await toDataURL(qrCodeUrl);
      if (!qrCode.isDisposed()) { qrCode.set(qrCodeImg); }
    })
    .catch((e) => { ctl.close(); reportError(e); });

  return dom.domComputed(qrCode, (code) => {
    if (code === null) { return mfaCss.spinner(loadingSpinner()); }

    return [
      mfaCss.formHeading("Configure authenticator app", testId("title")),
      mfaCss.formBody(
        dom("form",
          mfaCss.formInstructions(
            "An authenticator app lets you access your security code without receiving a text " +
            "message. If you don't already have an authenticator app, we'd recommend using ",
            cssLink("Google Authenticator", {
              href: "https://play.google.com/store/apps/" +
                "details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US",
              target: "_blank",
            }),
            ".",
          ),
          cssBoldSubHeading("To configure your authenticator app:"),
          cssListItem("1. Add a new account"),
          cssListItem("2. Scan the following QR code", { style: "margin-bottom: 8px" }),
          cssQRCode({ src: code }, testId("qr-code")),
          cssListItem("3. Enter the authentication code that appears after scanning the QR code"),
          dom.maybe(errorObs, err => mfaCss.formError(err, testId("form-error"))),
          mfaCss.formLabel("Authentication code", { for: "verificationCode" }),
          mfaCss.codeInput(verificationCode,
            verificationCodeInputOpts,
            testId("verification-code-input"),
          ),
          handleSubmit({
            pending,
            onSubmit: ({ verificationCode: c }) => loginAPI.confirmRegisterSoftwareToken(c),
            onSuccess: () => onSuccess(),
            onError: err => handleFormError(err, errorObs),
          }),
          mfaCss.formButtons(
            mfaCss.bigPrimaryButton("Verify", dom.boolAttr("disabled", pending), testId("verify")),
            mfaCss.bigBasicButton("Cancel", dom.on("click", () => ctl.close()), testId("cancel")),
          ),
        ),
      ),
    ];
  });
}

interface ConfigurePhoneMessageFormOptions {
  // Called on successful completion of the enable phone message forms.
  onSuccess: (newPhoneNumberMasked: string) => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
  // If true, shows a back text button on the first screen of the form.
  showBackButton?: boolean;
  // The text to use for the back button. Defaults to '← Back'.
  backButtonText?: string;
  // Called when the back button is clicked.
  onBack?: () => void;
}

/**
 * Builds a form for registering a SMS MFA method.
 *
 * @param {ConfigurePhoneMessageFormOptions} options Form options.
 */
export function buildConfigurePhoneMessageForms(
  owner: IDisposableOwner,
  { onSuccess, loginAPI, ctl, showBackButton, backButtonText, onBack }: ConfigurePhoneMessageFormOptions,
) {
  const currentStep = Observable.create<"enter-phone" | "verify-phone">(owner, "enter-phone");
  const phoneNumber = Observable.create(owner, "");

  let maskedPhoneNumber: string;

  return dom.domComputed(currentStep, (step) => {
    switch (step) {
      case "enter-phone": {
        return dom.create(buildRegisterPhoneNumberForm, phoneNumber, {
          onSuccess: (maskedCodeDeliveryDestination) => {
            maskedPhoneNumber = maskedCodeDeliveryDestination;
            currentStep.set("verify-phone");
          },
          loginAPI,
          ctl,
          showBackButton,
          backButtonText,
          onBack,
        });
      }
      case "verify-phone": {
        return dom.create(buildVerifyPhoneNumberForm, phoneNumber, maskedPhoneNumber, {
          onSuccess: () => onSuccess(maskedPhoneNumber),
          loginAPI,
          ctl,
          onBack: () => currentStep.set("enter-phone"),
        });
      }
    }
  });
}

interface RegisterPhoneNumberFormOptions {
  // Called on successful completion of the form.
  onSuccess: (maskedCodeDeliveryDestination: string) => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
  // If true, shows a back text button on the first screen of the form.
  showBackButton?: boolean;
  // The text to use for the back button. Defaults to '← Back'.
  backButtonText?: string;
  // Called when the back button is clicked.
  onBack?: () => void;
}

/**
 * Builds a form for registering a phone number for SMS-based 2FA.
 *
 * @param {RegisterPhoneNumberFormOptions} options Form options.
 */
function buildRegisterPhoneNumberForm(
  owner: IDisposableOwner,
  phoneNumber: Observable<string>,
  { onSuccess, loginAPI, ctl, showBackButton, backButtonText, onBack }: RegisterPhoneNumberFormOptions,
) {
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  return dom.frag(
    mfaCss.formHeading("Configure phone message", testId("title")),
    mfaCss.formBody(
      dom("form",
        mfaCss.formInstructions(
          "You need to add a U.S. phone number where you can receive authentication codes by text.",
        ),
        dom.maybe(errorObs, err => mfaCss.formError(err, testId("form-error"))),
        mfaCss.formLabel("Phone number", { for: "phoneNumber" }),
        mfaCss.input(phoneNumber,
          {
            id: "phoneNumber",
            name: "phoneNumber",
            placeholder: "+1 (201) 555 0123",
            autocomplete: "tel",
            type: "tel",
            required: "true",
          },
          (el) => { setTimeout(() => el.focus(), 10); },
          testId("phone-number-input"),
        ),
        handleSubmit({
          pending,
          onSubmit: ({ phoneNumber: phone }) => loginAPI.registerSMS(phone),
          onSuccess: ({ deliveryDestination }) => onSuccess(deliveryDestination),
          onError: err => handleFormError(err, errorObs),
        }),
        mfaCss.formButtons(
          mfaCss.bigPrimaryButton("Send code", dom.boolAttr("disabled", pending), testId("send-code")),
          mfaCss.bigBasicButton("Cancel", dom.on("click", () => ctl.close()), testId("cancel")),
        ),
      ),
    ),
    showBackButton && onBack ?
      mfaCss.formFooter(mfaCss.textButton(backButtonText ?? "← Back",
        { type: "button" },
        dom.on("click", () => onBack()),
        testId("back"),
      )) : null,
  );
}

interface VerifyPhoneNumberFormOptions {
  // Called on successful completion of the form.
  onSuccess: () => void;
  // An instance of LoginAPI.
  loginAPI: LoginAPI;
  // The control for the modal container of the form.
  ctl: IModalControl;
  // Called when the back button is clicked.
  onBack: () => void;
}

/**
 * Builds a form for verifying a new phone number for SMS-based 2FA.
 *
 * @param {VerifyPhoneNumberFormOptions} options Form options.
 */
function buildVerifyPhoneNumberForm(
  owner: IDisposableOwner,
  phoneNumber: Observable<string>,
  maskedPhoneNumber: string,
  { onSuccess, loginAPI, ctl, onBack }: VerifyPhoneNumberFormOptions,
) {
  const verificationCode = Observable.create(owner, "");
  const isResendingCode = Observable.create(owner, false);
  const errorObs: Observable<string | null> = Observable.create(owner, null);
  const pending = Observable.create(owner, false);

  owner.autoDispose(isResendingCode.addListener((isResending) => {
    if (!isResending) { return; }

    errorObs.set(null);
    verificationCode.set("");
  }));

  return dom.frag(
    dom.domComputed(isResendingCode, (isLoading) => {
      if (isLoading) { return mfaCss.spinner(loadingSpinner()); }

      return [
        mfaCss.formHeading("Confirm your phone", testId("title")),
        mfaCss.formBody(
          dom("form",
            mfaCss.formInstructions(
              mfaCss.text(
                "We have sent the authentication code to ",
                cssLightlyBoldedText(maskedPhoneNumber),
                ".",
              ),
              mfaCss.text("Enter it below to confirm your account."),
              testId("main-text"),
            ),
            dom.maybe(errorObs, err => mfaCss.formError(err, testId("form-error"))),
            mfaCss.formLabel("Authentication Code", { for: "verificationCode" }),
            mfaCss.codeInput(verificationCode,
              { ...verificationCodeInputOpts, autocomplete: "one-time-code" },
              (el) => { setTimeout(() => el.focus(), 10); },
              testId("verification-code-input"),
            ),
            mfaCss.resendCode("Didn't receive a code? ",
              mfaCss.textButton("Resend it",
                mfaCss.textButton.cls("-hover-bg-padding-sm"),
                { type: "button" },
                dom.on("click", async () => {
                  try {
                    isResendingCode.set(true);
                    await loginAPI.registerSMS(phoneNumber.get());
                  }
                  catch (err) {
                    handleFormError(err, errorObs);
                  }
                  finally {
                    isResendingCode.set(false);
                  }
                }),
                dom.boolAttr("disabled", pending),
                testId("resend-code"),
              ),
            ),
            handleSubmit({
              pending,
              onSubmit: ({ verificationCode: code }) => loginAPI.confirmRegisterSMS(code),
              onSuccess: () => onSuccess(),
              onError: err => handleFormError(err, errorObs),
            }),
            mfaCss.formButtons(
              mfaCss.bigPrimaryButton("Confirm", dom.boolAttr("disabled", pending), testId("confirm")),
              mfaCss.bigBasicButton("Cancel", dom.on("click", () => ctl.close()), testId("cancel")),
            ),
          ),
        ),
        mfaCss.formFooter(mfaCss.textButton("← Back to phone number",
          { type: "button" },
          dom.on("click", () => onBack()),
          testId("back-to-phone"),
        )),
      ];
    }),
  );
}

const cssFooterButtons = styled("div", `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`);

const cssListItem = styled(mfaCss.text, `
  margin-bottom: 16px;
`);

const cssBoldSubHeading = styled("div", `
  font-weight: bold;
  margin-bottom: 16px;
`);

const cssLightlyBoldedText = styled("span", `
  font-weight: 500;
`);

const cssQRCode = styled("img", `
  width: 140px;
  height: 140px;
  margin-bottom: 16px;
`);
